import axios, { AxiosRequestConfig } from 'axios'

import {
  CompassAPIMultiSeriesChartData,
  getCompassAPIChartData,
  getMultipleLineChartProps,
} from '../utils/compass'
import { capitalizeString } from '../utils/string'
import type { BaseContext } from './'
import { CommonChartConfig } from './common'

const NLP_MODELS_CHSRTS_TWITTER = '/api/charts/nlpmodels/twitter'

export namespace NlpModels {
  export namespace Twitter {
    const CONTEXT_ID = 'equity:nlpmodels:twitter'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'negative_sentiment_comparison'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const postNlpModelsTwitterChartData = (
      config: CommonChartConfig,
      requestModifier: (config: AxiosRequestConfig) => AxiosRequestConfig = (
        conf
      ) => conf
    ) =>
      getCompassAPIChartData(config, (config: AxiosRequestConfig) =>
        requestModifier({
          ...config,
          method: 'POST',
          data: {
            presidents: ['jair bolsonaro'],
            mention_user: ['@jairbolsonaro'],
          },
        })
      )

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: postNlpModelsTwitterChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartPropsWrapper(config, data),
    }

    const getMultipleLineChartPropsWrapper = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData,
      msdIndicators: { avg: boolean; sd1: boolean; sd2: boolean } = {
        avg: false,
        sd1: false,
        sd2: false,
      }
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          msdIndicators: msdIndicators,
          yAxis: [
            {
              id: 'twitter_negative_ratio',
              title: { text: 'Twitter negative ratio' },
            },
            {
              id: 'disaproval_index_jota',
              title: { text: 'Disaproval index (JOTA)' },
              opposite: true,
            },
          ],
        },
        {
          yAxis: data.data.map(({ name }) =>
            name.toLowerCase().includes('(jota)')
              ? 'disaproval_index_jota'
              : 'twitter_negative_ratio'
          ),
        }
      )(config, data)
  }

  export namespace TwitterThemeMention {
    const CONTEXT_ID = 'equity:nlpmodels:twitter_theme_mention'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      title?: string
      theme?: string
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: NLP_MODELS_CHSRTS_TWITTER + '/temas',
        method: 'POST',
        data: { theme: config.theme, min_date: new Date('2019-01-01') },
      }).then(
        (response) =>
          ({
            ...response.data,
            data: response.data.data.map((series: any) => ({
              ...series,
              data: series.values,
            })),
          } as ChartData)
      )

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {},
        { name: data.data.map((s) => capitalizeString(s.name)) }
      )(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.theme}`,
      getChartData,
      getChartProps,
    }
  }
}
